import { internalFetch as fetch } from './internalFetch';

// Use Node.js crypto module as fallback when Web Crypto API is unavailable (Node < 19, Jest)
let nodeCrypto;
try {
    if (typeof crypto === 'undefined' || !crypto.getRandomValues) {
        nodeCrypto = require('crypto');
    }
} catch (e) {
    // In browser environments, require() will fail — that's fine, we use Web Crypto
}

/**
 * Generate a cryptographically random PKCE code verifier (base64url, 32 bytes).
 * Works in browsers (Web Crypto) and Node.js (crypto module fallback).
 * @returns {string} base64url-encoded code verifier
 */
export const generateCodeVerifier = () => {
    let array;
    if (nodeCrypto) {
        array = new Uint8Array(nodeCrypto.randomBytes(32));
    } else {
        array = new Uint8Array(32);
        crypto.getRandomValues(array);
    }
    return btoa(String.fromCharCode(...array))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
};

/**
 * Generate a PKCE code challenge from a verifier using SHA-256.
 * @param {string} verifier - The code verifier string
 * @returns {Promise<string>} base64url-encoded code challenge
 */
export const generateCodeChallenge = async (verifier) => {
    let digest;
    if (nodeCrypto) {
        const hash = nodeCrypto.createHash('sha256').update(verifier).digest();
        digest = hash;
    } else {
        const encoder = new TextEncoder();
        const data = encoder.encode(verifier);
        digest = new Uint8Array(await crypto.subtle.digest('SHA-256', data));
    }
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
};

/**
 * Generate the full Deriv OAuth2 authorization URL with PKCE.
 * Returns the code_verifier alongside the URL — the caller is responsible for
 * persisting it (e.g. sessionStorage) and passing it back into exchangeToken().
 *
 * @param {string} clientId - Your Deriv app client ID
 * @param {string} redirectUri - The callback URL registered with Deriv
 * @param {string} [scope='trade account_manage'] - OAuth scopes
 * @param {string} [appId] - Optional legacy v1 app ID from legacy-api.deriv.com
 * @returns {Promise<{ url: string, codeVerifier: string }>}
 */
export const getAuthUrl = async (clientId, redirectUri, scope = 'trade account_manage', appId) => {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    let url = `https://auth.deriv.com/oauth2/auth?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&state=redirect&code_challenge=${codeChallenge}&code_challenge_method=S256&scope=${encodeURIComponent(scope)}`;

    if (appId) {
        url += `&app_id=${encodeURIComponent(appId)}`;
    }

    return { url, codeVerifier };
};

/**
 * Extract the authorization code from a Deriv OAuth callback URL.
 * @param {string} url - The full redirect URL containing the code parameter
 * @returns {string|null} The authorization code, or null if not found
 */
export const extractCodeFromUrl = (url) => {
    try {
        const urlObj = new URL(url);
        return urlObj.searchParams.get('code');
    } catch (e) {
        return null;
    }
};

/**
 * Generate the full Deriv OAuth2 signup authorization URL with PKCE (V4 only).
 * Includes additional affiliate tracking and registration parameters.
 *
 * @param {string} clientId - Your Deriv app client ID (e.g. app12345)
 * @param {string} redirectUri - The callback URL registered with Deriv
 * @param {string} [scope='trade account_manage'] - OAuth scopes
 * @param {Object} [signupOptions] - Additional signup parameters
 * @param {string} [signupOptions.appId] - Optional legacy v1 app ID from legacy-api.deriv.com
 * @param {string} [signupOptions.state] - Optional state parameter (random string for CSRF protection)
 * @param {string} [signupOptions.sidc] - Optional session GUID (e.g. 0FB46285-28A0-425E-B2E4-74F07D51EBB8)
 * @param {string} [signupOptions.utmCampaign] - Optional campaign name (e.g. dynamicworks)
 * @param {string} [signupOptions.utmMedium] - Optional medium (e.g. affiliate)
 * @param {string} [signupOptions.utmSource] - Optional affiliate ID (e.g. CU303219)
 * @returns {Promise<{ url: string, codeVerifier: string }>}
 * @description V4 only. For V3/V4 login use getAuthUrl()
 */
export const getSignupUrl = async (clientId, redirectUri, scope = 'trade account_manage', signupOptions = {}) => {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = signupOptions.state || 'redirect';

    let url = `https://auth.deriv.com/oauth2/auth?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scope)}&state=${encodeURIComponent(state)}&code_challenge=${codeChallenge}&code_challenge_method=S256&prompt=registration`;

    // Add optional parameters
    if (signupOptions.appId) {
        url += `&app_id=${encodeURIComponent(signupOptions.appId)}`;
    }
    if (signupOptions.sidc) {
        url += `&sidc=${encodeURIComponent(signupOptions.sidc)}`;
    }
    if (signupOptions.utmCampaign) {
        url += `&utm_campaign=${encodeURIComponent(signupOptions.utmCampaign)}`;
    }
    if (signupOptions.utmMedium) {
        url += `&utm_medium=${encodeURIComponent(signupOptions.utmMedium)}`;
    }
    if (signupOptions.utmSource) {
        url += `&utm_source=${encodeURIComponent(signupOptions.utmSource)}`;
    }

    return { url, codeVerifier };
};

/**
 * Exchange an authorization code for access tokens using PKCE.
 *
 * codeVerifier is required — it must be the same value returned by
 * getAuthUrl()/getSignupUrl() for this flow. Throws if omitted.
 *
 * @param {string} clientId - Your Deriv app client ID
 * @param {string} redirectUri - The callback URL (must match the one used in getAuthUrl or getSignupUrl)
 * @param {string} code - The authorization code from the callback URL
 * @param {string} codeVerifier - The code verifier returned by getAuthUrl() or getSignupUrl()
 * @returns {Promise<{ access_token: string, expires_in: number }>}
 */
export const exchangeToken = async (clientId, redirectUri, code, codeVerifier) => {
    if (!codeVerifier) {
        throw new Error(
            'PKCE codeVerifier is required for exchangeToken. Ensure you pass the verifier generated by getAuthUrl() or getSignupUrl().'
        );
    }

    const response = await fetch('https://auth.deriv.com/oauth2/token', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: clientId,
            code: code,
            redirect_uri: redirectUri,
            code_verifier: codeVerifier,
        }),
    });

    const data = await response.json();

    if (data.error) {
        throw new Error(`OAuth token exchange failed: ${data.error_description || data.error}`);
    }

    return data;
};
